import React, { useState } from 'react';
import { Printer } from 'lucide-react';
import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableRow,
  TableCell,
} from '@roger/ui-components';
import { getActiveBillingCycles } from '../../../services/api';
import type {
  ActiveBillingCycleItem,
  EpisodicClaimStatus,
} from '../../../services/api/types.gen';
import { formatDateToMMDDYYYY } from '../../../utils/dateUtils';
import { useBillingCycles } from '../../../hooks/useBillingCycles';

interface EOEPendingPaymentEntryProps {
  entry: ActiveBillingCycleItem;
  index?: number;
  isSelected: boolean;
  onToggle: (id: string) => void;
  onPrint: (entry: ActiveBillingCycleItem) => void;
}

const EOEPendingPaymentEntry: React.FC<EOEPendingPaymentEntryProps> = ({
  entry,
  index,
  isSelected,
  onToggle,
  onPrint,
}) => {
  const startDate = formatDateToMMDDYYYY(entry.billing_period_start);
  const endDate = formatDateToMMDDYYYY(entry.billing_period_end);
  const isAlternate = index !== undefined && index % 2 === 1;

  return (
    <TableRow
      className={
        isAlternate
          ? 'bg-muted hover:bg-muted'
          : 'bg-background hover:bg-background'
      }
    >
      <TableCell>
        <Checkbox
          checked={isSelected}
          onCheckedChange={() => onToggle(entry.billing_cycle_id)}
        />
      </TableCell>
      <TableCell>{entry.patient_name}</TableCell>
      <TableCell>{entry.patient_mrn}</TableCell>
      <TableCell>{entry.branch}</TableCell>
      <TableCell>
        <span>{startDate} -</span>
        <br />
        <span>{endDate}</span>
      </TableCell>
      <TableCell>{entry.claim_number}</TableCell>
      <TableCell>{entry.batch_id}</TableCell>
      <TableCell>{entry.total_anticipated_payment}</TableCell>
      <TableCell>{entry.total_charges}</TableCell>
      <TableCell>
        <Button
          type='button'
          variant='ghost'
          size='icon'
          onClick={() => onPrint(entry)}
          className='text-muted-foreground hover:text-foreground'
          aria-label='Print billing cycle'
        >
          <Printer className='h-4 w-4' />
        </Button>
      </TableCell>
    </TableRow>
  );
};

const EOEPendingPaymentTab: React.FC = () => {
  const [printEntry, setPrintEntry] = useState<ActiveBillingCycleItem | null>(
    null
  );
  const [pushIdsOpen, setPushIdsOpen] = useState(false);

  const { data, isLoading, selectedIds, handleToggleId, handleToggleAll } =
    useBillingCycles<
      ActiveBillingCycleItem,
      { statuses: EpisodicClaimStatus[] }
    >({
      fetchFn: getActiveBillingCycles,
      fetchPayload: { statuses: ['PENDING_PAYMENT'] },
      errorMessage: 'Could not fetch data for the "Pending Payment" tab.',
      getId: item => item.billing_cycle_id,
      getName: item => item.patient_name,
    });

  return (
    <div className='flex flex-col gap-4'>
      {isLoading ? (
        <p className='text-muted-foreground text-center py-10'>Loading...</p>
      ) : data.length === 0 ? (
        <p className='text-muted-foreground text-center py-10'>
          There are currently no records to display
        </p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>
                <Checkbox
                  checked={data.length > 0 && selectedIds.size === data.length}
                  onCheckedChange={handleToggleAll}
                />
              </TableHead>
              <TableHead>Patient Name</TableHead>
              <TableHead>MRN</TableHead>
              <TableHead>Branch</TableHead>
              <TableHead>Billing Period</TableHead>
              <TableHead>Claim #</TableHead>
              <TableHead>Batch ID</TableHead>
              <TableHead>Total Anticipated Payment</TableHead>
              <TableHead>Total Charges</TableHead>
              <TableHead>Print</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.map((entry, index) => (
              <EOEPendingPaymentEntry
                key={entry.billing_cycle_id}
                entry={entry}
                index={index}
                isSelected={selectedIds.has(entry.billing_cycle_id)}
                onToggle={handleToggleId}
                onPrint={setPrintEntry}
              />
            ))}
          </TableBody>
        </Table>
      )}
      <Dialog
        open={printEntry !== null}
        onOpenChange={open => !open && setPrintEntry(null)}
      >
        <DialogContent className='max-w-2xl max-h-[80vh] overflow-hidden flex flex-col'>
          <DialogHeader>
            <DialogTitle>Billing Cycle Details</DialogTitle>
          </DialogHeader>
          <pre className='overflow-auto flex-1 text-xs bg-muted rounded p-4 whitespace-pre-wrap break-all'>
            {JSON.stringify(printEntry, null, 2)}
          </pre>
        </DialogContent>
      </Dialog>
      <Dialog open={pushIdsOpen} onOpenChange={setPushIdsOpen}>
        <DialogContent className='max-w-lg'>
          <DialogHeader>
            <DialogTitle>Selected billing cycle IDs</DialogTitle>
          </DialogHeader>
          <pre className='text-sm bg-muted rounded p-4 overflow-auto max-h-64 whitespace-pre-wrap break-all'>
            {Array.from(selectedIds).join('\n')}
          </pre>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EOEPendingPaymentTab;
